import React, { useState } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  IconButton,
  Tooltip,
  useTheme,
  alpha,
  LinearProgress,
  Avatar,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Badge,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Event as EventIcon,
  People as PeopleIcon,
  Star as StarIcon,
  Notifications as NotificationsIcon,
  Search as SearchIcon,
  CalendarToday as CalendarIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Language as WebsiteIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  Instagram as InstagramIcon,
  LinkedIn as LinkedInIcon,
  FilterList as FilterIcon,
  Sort as SortIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  Category as CategoryIcon,
  Store as StoreIcon,
  PhotoCamera as PhotoCameraIcon,
} from '@mui/icons-material';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend } from 'recharts';

const TestDashboard2 = () => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  // Demo data for YYCMarketgirls event
  const eventDetails = {
    id: 1,
    title: 'YYCMarketgirls Rooftop Market',
    date: '2024-07-15',
    location: 'Downtown Rooftop, Calgary',
    description: 'A curated market featuring local women entrepreneurs and artisans.',
    totalApplications: 50,
    approvedVendors: 25,
    pendingApplications: 15,
    rejectedApplications: 10,
    categories: ['Fashion', 'Art', 'Food', 'Beauty', 'Home Decor'],
    revenue: 25000,
    capacity: 30,
  };

  const vendorApplications = [
    {
      id: 1,
      name: 'Boho Chic Boutique',
      category: 'Fashion',
      description: 'Handmade bohemian clothing and accessories',
      rating: 4.9,
      status: 'Pending',
      previousEvents: 5,
      socialMedia: {
        instagram: '@bohochic',
        website: 'www.bohochic.com',
      },
      photos: ['photo1.jpg', 'photo2.jpg'],
      applicationDate: '2024-03-01',
    },
    {
      id: 2,
      name: 'Artisan Bakery Co.',
      category: 'Food',
      description: 'Specialty breads and pastries',
      rating: 4.8,
      status: 'Approved',
      previousEvents: 8,
      socialMedia: {
        instagram: '@artisanbakery',
        website: 'www.artisanbakery.com',
      },
      photos: ['photo3.jpg', 'photo4.jpg'],
      applicationDate: '2024-03-02',
    },
    {
      id: 3,
      name: 'Modern Art Studio',
      category: 'Art',
      description: 'Contemporary paintings and sculptures',
      rating: 4.7,
      status: 'Pending',
      previousEvents: 3,
      socialMedia: {
        instagram: '@modernart',
        website: 'www.modernart.com',
      },
      photos: ['photo5.jpg', 'photo6.jpg'],
      applicationDate: '2024-03-03',
    },
    // Add more vendors...
  ];

  const categoryStats = {
    Fashion: { total: 15, approved: 8, pending: 5, rejected: 2 },
    Art: { total: 12, approved: 6, pending: 4, rejected: 2 },
    Food: { total: 10, approved: 5, pending: 3, rejected: 2 },
    Beauty: { total: 8, approved: 4, pending: 2, rejected: 2 },
    'Home Decor': { total: 5, approved: 2, pending: 1, rejected: 2 },
  };

  // Data for pie chart
  const pieChartData = Object.entries(categoryStats).map(([name, stats]) => ({
    name,
    value: stats.total,
  }));

  // Data for bar chart
  const barChartData = Object.entries(categoryStats).map(([name, stats]) => ({
    name,
    approved: stats.approved,
    pending: stats.pending,
    rejected: stats.rejected,
  }));

  // Colors for charts
  const COLORS = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.error.main,
  ];

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
  };

  const handleCategoryFilterChange = (event) => {
    setCategoryFilter(event.target.value);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Approved':
        return 'success';
      case 'Pending':
        return 'warning';
      case 'Rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Event Header */}
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={8}>
            <Typography 
              variant="h4" 
              component="h1" 
              gutterBottom
              sx={{ 
                color: theme.palette.primary.main,
                fontWeight: 600,
              }}
            >
              {eventDetails.title}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {new Date(eventDetails.date).toLocaleDateString()} • {eventDetails.location}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button variant="contained" color="primary">
              Edit Event
            </Button>
            <Button variant="outlined" color="primary">
              Share Event
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Event Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card 
            sx={{ 
              height: '100%',
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.light, 0.2)} 100%)`,
            }}
          >
            <CardContent>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                  }}
                >
                  <PeopleIcon color="primary" />
                </Box>
                <Typography variant="h6" color="text.secondary">
                  Applications
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                {eventDetails.totalApplications}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Total Applications
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card 
            sx={{ 
              height: '100%',
              background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.light, 0.2)} 100%)`,
            }}
          >
            <CardContent>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                  }}
                >
                  <CheckCircleIcon color="success" />
                </Box>
                <Typography variant="h6" color="text.secondary">
                  Approved
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                {eventDetails.approvedVendors}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Approved Vendors
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card 
            sx={{ 
              height: '100%',
              background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${alpha(theme.palette.warning.light, 0.2)} 100%)`,
            }}
          >
            <CardContent>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.warning.main, 0.1),
                  }}
                >
                  <PendingIcon color="warning" />
                </Box>
                <Typography variant="h6" color="text.secondary">
                  Pending
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                {eventDetails.pendingApplications}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Pending Reviews
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card 
            sx={{ 
              height: '100%',
              background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.1)} 0%, ${alpha(theme.palette.error.light, 0.2)} 100%)`,
            }}
          >
            <CardContent>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.error.main, 0.1),
                  }}
                >
                  <CancelIcon color="error" />
                </Box>
                <Typography variant="h6" color="text.secondary">
                  Rejected
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                {eventDetails.rejectedApplications}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Rejected Applications
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Category Distribution with Charts */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Category Distribution
          </Typography>
          <Grid container spacing={3}>
            {/* Pie Chart */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Typography variant="subtitle1" gutterBottom>
                  Applications by Category
                </Typography>
                <Box sx={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieChartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {pieChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>

            {/* Bar Chart */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Typography variant="subtitle1" gutterBottom>
                  Status Distribution by Category
                </Typography>
                <Box sx={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={barChartData}
                      margin={{
                        top: 20,
                        right: 30,
                        left: 20,
                        bottom: 5,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <RechartsTooltip />
                      <Legend />
                      <Bar dataKey="approved" name="Approved" fill={theme.palette.success.main} />
                      <Bar dataKey="pending" name="Pending" fill={theme.palette.warning.main} />
                      <Bar dataKey="rejected" name="Rejected" fill={theme.palette.error.main} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>

            {/* Category Stats Cards */}
            {Object.entries(categoryStats).map(([category, stats]) => (
              <Grid item xs={12} sm={6} md={4} key={category}>
                <Paper sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CategoryIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                    <Typography variant="h6">{category}</Typography>
                  </Box>
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Total Applications
                    </Typography>
                    <Typography variant="h6">{stats.total}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box>
                      <Typography variant="body2" color="success.main">
                        Approved
                      </Typography>
                      <Typography variant="h6">{stats.approved}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="warning.main">
                        Pending
                      </Typography>
                      <Typography variant="h6">{stats.pending}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="error.main">
                        Rejected
                      </Typography>
                      <Typography variant="h6">{stats.rejected}</Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Vendor Applications */}
      <Card>
        <CardContent>
          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  placeholder="Search vendors..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    onChange={handleStatusFilterChange}
                    label="Status"
                  >
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="approved">Approved</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="rejected">Rejected</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={categoryFilter}
                    onChange={handleCategoryFilterChange}
                    label="Category"
                  >
                    <MenuItem value="all">All Categories</MenuItem>
                    {eventDetails.categories.map((category) => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Vendor</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Rating</TableCell>
                  <TableCell>Previous Events</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {vendorApplications.map((vendor) => (
                  <TableRow key={vendor.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar src={vendor.photos[0]} alt={vendor.name}>
                          <StoreIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2">{vendor.name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {vendor.description}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{vendor.category}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <StarIcon sx={{ color: theme.palette.warning.main }} />
                        <Typography>{vendor.rating}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{vendor.previousEvents}</TableCell>
                    <TableCell>
                      <Chip 
                        label={vendor.status} 
                        color={getStatusColor(vendor.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="contained"
                          color="success"
                          size="small"
                          startIcon={<CheckCircleIcon />}
                        >
                          Approve
                        </Button>
                        <Button
                          variant="contained"
                          color="error"
                          size="small"
                          startIcon={<CancelIcon />}
                        >
                          Reject
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Container>
  );
};

export default TestDashboard2; 